import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { ApprovalMark } from "@/payroll/approvals/approval-mark";
import { describeMark } from "@/payroll/approvals/approval-types";
import {
  getSheetApproval,
  getSheetApprovalSubject,
} from "@/payroll/approvals/utils";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { SheetSwitcher } from "./sheet-switcher";
import { useSheetTabScroll } from "./use-sheet-tab-scroll";
import { SHEET_PREFETCH_DWELL_MS } from "./utils";

// Excel-style sheet tab pinned to the bottom of the workbook frame. The sheet
// name leads and its compact approval mark trails without crowding the scroll
// controls at the end of the strip.
const SHEET_TAB_CLASSNAME = cn(
  "flex max-w-64 shrink-0 items-center gap-1.5 whitespace-nowrap border-r border-r-line border-t-2 border-t-transparent py-1.5 pl-3 pr-4",
  "text-sm text-subtle transition-colors",
  "hover:bg-neutral-subtle-hovered hover:text-ink",
  "data-active:border-t-selected-line data-active:bg-selected data-active:text-brand data-active:hover:bg-selected-hovered",
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
);

interface SheetTabListProps {
  activeSheetId: string;
  isSheetSwitcherOpen: boolean;
  sheets: IAiSpreadsheetSheet[];
  onSheetSwitcherOpenChange: (isOpen: boolean) => void;
  onSheetSelect: (sheetId: string) => void;
  onSheetPrefetchIntent: (sheetId: string) => void;
  /** Drives the per-tab approval marks. Setting them lives in the toolbar. */
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  rightAccessory?: ReactNode;
}

export const SheetTabList = ({
  activeSheetId,
  isSheetSwitcherOpen,
  sheets,
  onSheetSwitcherOpenChange,
  onSheetSelect,
  onSheetPrefetchIntent,
  approvalStatus,
  rightAccessory,
}: SheetTabListProps) => {
  const activeSheet = sheets.find((sheet) => sheet.id === activeSheetId);
  const {
    activeTabRef,
    canScrollLeft,
    canScrollRight,
    scrollbarHeight,
    scrollTabs,
    tabListRef,
    tabViewportRef,
  } = useSheetTabScroll({ activeSheetId, sheets });
  const { schedule: scheduleSheetPrefetch, cancel: cancelSheetPrefetch } =
    useDelayedAction<string>(onSheetPrefetchIntent, SHEET_PREFETCH_DWELL_MS);

  return (
    <Stack
      direction="row"
      align="stretch"
      gap={0}
      className="w-full shrink-0 border-t border-line bg-surface-sunken"
    >
      {/* A scrollbar the OS always shows sits under the tabs and adds to the
          strip's height. The controls either side leave the same room below,
          so they centre on the tabs rather than on the tabs and the bar. */}
      <Stack
        direction="row"
        align="stretch"
        gap={0}
        style={{ paddingBottom: scrollbarHeight }}
      >
        <SheetSwitcher
          activeSheetId={activeSheetId}
          isOpen={isSheetSwitcherOpen}
          sheets={sheets}
          onOpenChange={onSheetSwitcherOpenChange}
          onSheetSelect={onSheetSelect}
          onSheetPrefetchIntent={onSheetPrefetchIntent}
        />
      </Stack>

      {/* The strip draws its own Excel-style tabs, so it uses Base UI's tabs
          directly rather than the preset's styled ones. */}
      <div
        ref={tabViewportRef}
        data-testid="sheet-tab-viewport"
        className="quiet-scrollbar min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
      >
        <TabsPrimitive.List
          ref={tabListRef}
          activateOnFocus
          className="flex min-w-max items-stretch"
        >
          {sheets.map((sheet) => {
            const group = getSheetApproval(sheet, approvalStatus);
            const approval = group
              ? getSheetApprovalSubject(group, sheet.id)
              : undefined;

            return (
              <Stack
                key={sheet.id}
                direction="row"
                align="stretch"
                gap={0}
                className="group relative"
              >
                <TabsPrimitive.Tab
                  ref={sheet.id === activeSheetId ? activeTabRef : undefined}
                  value={sheet.id}
                  title={sheet.name}
                  className={SHEET_TAB_CLASSNAME}
                  onMouseEnter={() => scheduleSheetPrefetch(sheet.id)}
                  onMouseLeave={cancelSheetPrefetch}
                  onFocus={() => scheduleSheetPrefetch(sheet.id)}
                  onBlur={cancelSheetPrefetch}
                >
                  <span className="min-w-0 truncate" title={sheet.name}>
                    {sheet.name}
                  </span>
                  {approval && (
                    <span className="sr-only">{describeMark(approval)}</span>
                  )}
                  {approval && (
                    <ApprovalMark accessKey={approval} withTooltip />
                  )}
                </TabsPrimitive.Tab>
              </Stack>
            );
          })}
        </TabsPrimitive.List>
      </div>

      <Stack
        direction="row"
        align="stretch"
        gap={0}
        style={{ paddingBottom: scrollbarHeight }}
      >
        <SheetTabScrollButton
          direction="left"
          disabled={!canScrollLeft}
          onClick={() => scrollTabs(-1)}
        />
        <SheetTabScrollButton
          direction="right"
          disabled={!canScrollRight}
          onClick={() => scrollTabs(1)}
        />

        {rightAccessory}
      </Stack>

      <span className="sr-only" aria-live="polite">
        {activeSheet ? `Current sheet: ${activeSheet.name}` : "No sheet"}
      </span>
    </Stack>
  );
};

interface SheetTabScrollButtonProps {
  direction: "left" | "right";
  disabled: boolean;
  onClick: () => void;
}

const SheetTabScrollButton = ({
  direction,
  disabled,
  onClick,
}: SheetTabScrollButtonProps) => {
  const Icon = direction === "left" ? CaretLeftIcon : CaretRightIcon;

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={`Scroll sheet tabs ${direction}`}
      disabled={disabled}
      onClick={onClick}
      className="self-center"
    >
      <Icon />
    </Button>
  );
};
